import { ArrowRight, Check, Loader2, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";
import { CohortAccessRequest } from "~/pages/manifold-nextgen/components/cohorts/interfaces/access_requests";

export type ApprovalStreamStatus = "idle" | "streaming" | "done" | "error";

export interface ProgressItem {
  message: string;
  status: "loading" | "done";
}

export interface ChildDatasetExportPanelState {
  requestId: string;
  datasetName: string;
  streamStatus: ApprovalStreamStatus;
  progressItems: ProgressItem[];
  resultDatasetId: string | null;
  resultWarnings: string[];
  streamError: string | null;
}

interface ChildDatasetExportPanelProps {
  request: CohortAccessRequest;
  panel: ChildDatasetExportPanelState;
  onDatasetNameChange: (name: string) => void;
  onExport: () => void;
}

export function ChildDatasetExportPanel({
  request,
  panel,
  onDatasetNameChange,
  onExport,
}: ChildDatasetExportPanelProps) {
  const isStreaming = panel.streamStatus === "streaming";
  const isDone = panel.streamStatus === "done";
  const showProgress = panel.streamStatus !== "idle";

  return (
    <>
      <div className="flex gap-2">
        <Input
          id={`dataset-name-${request.id}`}
          value={panel.datasetName}
          onChange={(e) => onDatasetNameChange(e.target.value)}
          disabled={isStreaming || isDone}
          className="flex-1"
        />
        <Button
          onClick={onExport}
          disabled={isStreaming || isDone || !panel.datasetName.trim()}
          loading={isStreaming}
        >
          Export as dataset
          <ArrowRight />
        </Button>
      </div>

      {showProgress && (
        <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 px-4 py-3 space-y-2">
          <p className="text-sm font-semibold text-green-800 dark:text-green-400">
            Export progress
          </p>
          <ul className="space-y-1.5">
            {panel.progressItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                {item.status === "loading" ? (
                  <Loader2
                    className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                ) : (
                  <Check
                    className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-400"
                    aria-hidden
                  />
                )}
                <span
                  className={cn(
                    item.status === "done" ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {item.message}
                </span>
              </li>
            ))}
            {panel.streamStatus === "error" && panel.streamError && (
              <li className="flex items-start gap-2 text-sm">
                <X className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
                <span className="text-destructive">{panel.streamError}</span>
              </li>
            )}
          </ul>
          {isDone && panel.resultWarnings.length > 0 && (
            <ul className="space-y-1">
              {panel.resultWarnings.map((warning, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <X className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
                  <span className="text-amber-700 dark:text-amber-400">{warning}</span>
                </li>
              ))}
            </ul>
          )}
          {isDone && panel.resultDatasetId && (
            <a
              href={`/nova/datasets/${panel.resultDatasetId}`}
              className="inline-flex items-center gap-1 rounded-lg border border-green-600 px-3 py-1.5 text-sm font-semibold text-green-700 hover:bg-green-100 dark:text-green-400 dark:border-green-600 dark:hover:bg-green-900/30"
            >
              Open dataset in catalog →
            </a>
          )}
        </div>
      )}
    </>
  );
}
